<script setup lang="ts">
import { usePermissionsDialogStore } from '#auth/composables/usePermissionsDialogStore'
import { ProcessingAnimation } from '@forgd/ui-kit/blocks/animations'
import { Button } from '@forgd/ui-kit/button'
import { Dialog, DialogContent } from '@forgd/ui-kit/dialog'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/vue/24/outline'
import { useMutation } from '@pinia/colada'

const authStore = useAuth()
const permissionsDialogStore = usePermissionsDialogStore()
const { isSwitchModalOpen, type, state } = storeToRefs(permissionsDialogStore)
const { closeSwitchModal } = permissionsDialogStore
const { project } = storeToRefs(authStore)

const { mutateAsync: promoteResearch } = useMutation(
  buildApiMutation('/projects/{projectId}/promote-research', { method: 'patch', options: { toast: true } }),
)

async function handleConfirm() {
  if (!project.value) {
    return
  }

  try {
    state.value = 'processing'
    await Promise.all([
      promoteResearch({
        path: {
          projectId: project.value.id,
        },
      }),
      // Make sure that the processing state is visible for 3 seconds
      new Promise(resolve => setTimeout(resolve, 3000)),
    ])

    state.value = 'success'
    authStore.refresh({ from: 'switch-account-profile-modal' })
  }
  catch {
    state.value = 'prompt'
  }
}

async function handleEditTokenDetails() {
  navigateTo(getProjectPath('/token-settings'))
  closeSwitchModal()
}

const isModalOpen = computed({
  get: () => isSwitchModalOpen.value,
  set: (open) => {
    if (open) {
      return
    }
    closeSwitchModal()
  },
})
</script>

<template>
  <Dialog v-model:open="isModalOpen">
    <DialogContent>
      <div v-if="state === 'prompt'" class="flex flex-col gap-5">
        <div>
          <ExclamationCircleIcon class="size-10 text-accent" aria-hidden="true" />
          <div class="mb-3 font-semibold text-xl text-primal">
            {{ type === 'confirm-switch'
              ? 'You are about to switch the account profile.'
              : 'You are currently working with a researcher profile account.'
            }}
          </div>
          <div class="mb-2 text-secondary text-base space-y-4">
            <template v-if="type === 'confirm-switch'">
              <p>Don't worry - your progress will be saved, and no work will be lost.</p>
              <p>Do you want to continue?</p>
            </template>
            <p v-else>
              To continue with this feature, you'll need to update your account to a “project” profile. Don't worry - your progress will be saved, and no work will be lost.
            </p>
          </div>
        </div>
        <div class="flex items-center w-full gap-3">
          <Button variant="outline" class="grow basis-0" @click="closeSwitchModal">
            Cancel
          </Button>
          <Button class="grow basis-0" @click="handleConfirm">
            {{ type === 'confirm-switch' ? 'Continue' : 'Switch Account Profile' }}
          </Button>
        </div>
      </div>
      <div v-else-if="state === 'processing'" class="flex flex-col items-center justify-center">
        <ProcessingAnimation size="md" />
        <div class="mb-3 font-semibold text-xl text-primal">
          We are processing your submission...
        </div>
        <div class="text-secondary text-base">
          Do not close this window
        </div>
      </div>
      <div v-else-if="state === 'success'" class="flex flex-col gap-5">
        <div>
          <CheckCircleIcon class="size-10 text-green-600" aria-hidden="true" />
          <div class="mb-3 font-semibold text-xl text-primal">
            Your account has been updated
          </div>
          <div class="mb-2 text-secondary text-base">
            Your account is now set up with full access to all Forgd tools. We recommend customizing your token details (name, ticker, image, etc.) before proceeding with other tasks.
          </div>
        </div>
        <div class="flex items-center w-full gap-3">
          <Button v-if="type === 'confirm-switch'" variant="outline" class="w-full" @click="closeSwitchModal">
            Got it
          </Button>
          <template v-if="type === 'require-switch'">
            <Button variant="outline" class="grow basis-0" @click="closeSwitchModal">
              I Will Do This Later
            </Button>
            <Button class="grow basis-0" @click="handleEditTokenDetails">
              Edit Token Details
            </Button>
          </template>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
